import React, { useEffect, useRef } from 'react';
import { sectionLabelClass } from '../ChallengeWire/challengeWireFormStyles';
import { buildChallengeScheduleLine } from '../../utils/challengeSummaryDisplay';
import { AI_TRAINER_PERSONALITIES } from '../../constants/aiTrainerPersonalities';
import {
	PERSONA_BORDER,
	aiTrainerCardClassName,
	aiTrainerInputClassName,
	aiTrainerMutedTextClassName,
	aiTrainerPrimaryButtonClassName
} from './aiTrainerStyles';
import WorkoutPlanCard from './WorkoutPlanCard';
import MealBreakdownCard from './MealBreakdownCard';

const toolChipClassName =
	'inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-blue-400 dark:border-dark-border dark:bg-dark-card dark:text-dark-text dark:hover:border-blue-500';

function SuggestedChallengeCard({ action, onAccept, accepting }) {
	const sectionLabel = action.source === 'workout_plan' ? 'Workout challenge' : 'New challenge idea';
	const scheduleLine = buildChallengeScheduleLine(action.starts_on, action.ends_on);

	return (
		<div className="rounded-xl border border-dashed border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4">
			<p className={sectionLabelClass}>{sectionLabel}</p>
			<p className="mt-2 text-base font-semibold text-gray-900 dark:text-dark-text">{action.title}</p>
			{action.reason && <p className={`mt-1 text-sm ${aiTrainerMutedTextClassName}`}>{action.reason}</p>}
			{scheduleLine && (
				<p className="mt-2 text-sm leading-relaxed text-gray-800 dark:text-dark-text">{scheduleLine}</p>
			)}
			<button
				type="button"
				disabled={accepting}
				onClick={() => onAccept(action.confirmation_token)}
				className={`mt-3 ${aiTrainerPrimaryButtonClassName}`}
			>
				{accepting ? 'Creating…' : 'Start this challenge'}
			</button>
		</div>
	);
}

function CoachMessage({ message, personality, onAcceptChallenge, acceptingToken }) {
	const actions = message.metadata?.suggested_actions || [];
	const workoutPlan = message.metadata?.workout_plan;
	const mealBreakdown = message.metadata?.meal_breakdown;
	const borderClass = PERSONA_BORDER[personality] || PERSONA_BORDER.accountability_partner;

	return (
		<div className="flex max-w-xl flex-col gap-3">
			<article
				className={`rounded-2xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card border-l-4 ${borderClass} px-4 py-3 shadow-sm`}
			>
				<p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800 dark:text-dark-text">
					{message.content}
				</p>
			</article>
			{workoutPlan && <WorkoutPlanCard plan={workoutPlan} />}
			{mealBreakdown && <MealBreakdownCard breakdown={mealBreakdown} />}
			{actions.map((action) => (
				<SuggestedChallengeCard
					key={action.confirmation_token}
					action={action}
					onAccept={onAcceptChallenge}
					accepting={acceptingToken === action.confirmation_token}
				/>
			))}
		</div>
	);
}

function UserMessage({ message }) {
	const isMealAnalysis = Boolean(message.metadata?.meal_log);

	return (
		<div className="flex justify-end">
			<div className="max-w-xl rounded-2xl bg-blue-600 px-4 py-3 text-sm leading-relaxed text-white shadow-sm">
				{isMealAnalysis && (
					<p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-100">
						<i className="fa-solid fa-camera text-[11px]" aria-hidden="true" />
						Meal analysis
					</p>
				)}
				<p className="whitespace-pre-wrap">{message.content}</p>
			</div>
		</div>
	);
}

export default function AiTrainerChat({
	messages,
	personality,
	fitnessProfileId,
	input,
	onInputChange,
	onSend,
	sending,
	analyzingMeal = false,
	onMealPhotoSelected,
	onAcceptChallenge,
	acceptingToken,
	quickPrompts,
	onQuickPrompt,
	onClearChat,
	clearingChat
}) {
	const scrollContainerRef = useRef(null);
	const composerRef = useRef(null);
	const messageRefs = useRef({});
	const mealInputRef = useRef(null);
	const persona = AI_TRAINER_PERSONALITIES.find((item) => item.id === personality);
	const composerBusy = sending || analyzingMeal || clearingChat;

	function openMealPhotoPicker() {
		if (composerBusy || !onMealPhotoSelected) return;
		mealInputRef.current?.click();
	}

	useEffect(() => {
		if (!fitnessProfileId) return;

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
			});
		});
	}, [fitnessProfileId]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container || messages.length === 0) return;

		let lastUserMessageId = null;
		for (let index = messages.length - 1; index >= 0; index -= 1) {
			if (messages[index].role === 'user') {
				lastUserMessageId = messages[index].id;
				break;
			}
		}

		if (!lastUserMessageId) return;

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const target = messageRefs.current[lastUserMessageId];
				if (!target) return;

				const containerRect = container.getBoundingClientRect();
				const targetRect = target.getBoundingClientRect();
				container.scrollTop += targetRect.top - containerRect.top - 12;
			});
		});
	}, [messages, sending]);

	return (
		<div className={`flex min-h-[28rem] flex-col overflow-hidden lg:min-h-[32rem] ${aiTrainerCardClassName}`}>
			<div className="border-b border-gray-200 dark:border-dark-border px-4 py-3 sm:px-5 sm:py-4">
				<div className="flex items-center justify-between gap-3">
					<p className={`${sectionLabelClass} min-w-0 truncate`}>Coach chat</p>
					{onClearChat && messages.length > 0 && (
						<button
							type="button"
							onClick={onClearChat}
							disabled={composerBusy}
							className={`shrink-0 whitespace-nowrap rounded-xl border border-gray-300 dark:border-dark-border px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg disabled:opacity-60 transition sm:px-4 sm:py-2 sm:text-sm`}
						>
							{clearingChat ? 'Clearing…' : 'New chat'}
						</button>
					)}
				</div>
				<p className="mt-1 text-xs leading-snug text-gray-600 dark:text-dark-text-secondary sm:text-sm sm:leading-relaxed">
					{persona
						? `${persona.emoji} — ask about training, nutrition, or recovery.`
						: 'Ask about training, nutrition, or recovery.'}
				</p>
			</div>

			<div ref={scrollContainerRef} className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
				{messages.length === 0 && (
					<div className="flex h-full min-h-[12rem] flex-col items-center justify-center px-4 text-center">
						{persona && <span className="text-4xl" aria-hidden="true">{persona.emoji}</span>}
						<p className={`mt-3 max-w-sm text-sm leading-relaxed ${aiTrainerMutedTextClassName}`}>
							Say hi, ask a training question, or tap a suggestion below.
						</p>
					</div>
				)}
				{messages.map((message) =>
					message.role === 'user' ? (
						<div
							key={message.id}
							ref={(node) => {
								if (node) messageRefs.current[message.id] = node;
							}}
						>
							<UserMessage message={message} />
						</div>
					) : (
						<CoachMessage
							key={message.id}
							message={message}
							personality={personality}
							onAcceptChallenge={onAcceptChallenge}
							acceptingToken={acceptingToken}
						/>
					)
				)}
				{sending && (
					<p className={`px-1 text-sm ${aiTrainerMutedTextClassName}`}>Coach is thinking…</p>
				)}
				{analyzingMeal && (
					<p className={`px-1 text-sm ${aiTrainerMutedTextClassName}`}>Analyzing your meal photo…</p>
				)}
			</div>

			<div
				ref={composerRef}
				className="border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-5 pb-4 pt-3 space-y-3"
			>
				{onMealPhotoSelected && (
					<input
						ref={mealInputRef}
						type="file"
						accept="image/png,image/jpeg,image/jpg,image/webp"
						capture="environment"
						className="sr-only"
						onChange={(event) => {
							const file = event.target.files?.[0];
							event.target.value = '';
							if (file) onMealPhotoSelected(file);
						}}
					/>
				)}

				<form
					className="flex gap-2"
					onSubmit={(event) => {
						event.preventDefault();
						onSend();
					}}
				>
					<input
						type="text"
						value={input}
						onChange={(event) => onInputChange(event.target.value)}
						placeholder="Ask your coach…"
						className={`flex-1 ${aiTrainerInputClassName}`}
					/>
					<button
						type="submit"
						disabled={composerBusy || !input.trim()}
						className={aiTrainerPrimaryButtonClassName}
					>
						Send
					</button>
				</form>

				{(onMealPhotoSelected || quickPrompts?.length > 0) && (
					<div className="-mx-5 space-y-2 border-t border-gray-200 px-5 pt-3 dark:border-dark-border">
						<p className={sectionLabelClass}>Tap to ask</p>
						<div className="flex flex-wrap gap-2">
							{onMealPhotoSelected && (
								<button
									type="button"
									disabled={composerBusy}
									onClick={openMealPhotoPicker}
									className={toolChipClassName}
								>
									<i className="fa-solid fa-camera text-blue-600 dark:text-blue-400" aria-hidden="true" />
									{analyzingMeal ? 'Analyzing…' : 'Analyze meal photo'}
								</button>
							)}
							{quickPrompts?.map((prompt) => (
								<button
									key={prompt}
									type="button"
									disabled={composerBusy}
									onClick={() => onQuickPrompt(prompt)}
									className={toolChipClassName}
								>
									{prompt}
								</button>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
